import { saveAs } from "file-saver";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  CatalogEntryChangeImpact,
  CatalogEntryChangeImpactInput,
  MasterProduct,
  MasterProductInput,
  MasterProductsResponse,
  ReclassifyCatalogEntryInput,
  ReclassifyCatalogEntryResponse
} from "./types/master-products.type";
import { ProductsFilters } from "./types/products.type";

export type BulkCreateHearingAidRow = {
  // Identity comes from EITHER a master product reference (Activate Existing —
  // the server creates only the practice Product from the master) OR the
  // hand-typed manufacturer/model/specification trio. When `master_product` is
  // sent the identity keys are omitted entirely; when it is not, the payload is
  // byte-identical to the original quick-add shape — an old backend silently
  // drops the unknown `master_product` key and would then 400 on the missing
  // identity fields, so the key only travels under
  // ACTIVATE_MASTER_PRODUCTS_FEATURE (the FE-first rule).
  master_product?: number;
  manufacturer?: number;
  model?: string;
  specification?: {
    type: number;
    technology: number;
  };
  cpt_code: number | null;
  // Additional-only CPT code ids (never contains cpt_code). Only sent when
  // MULTIPLE_CPT_CODES_FEATURE is on — an old backend rejects unknown keys.
  cpt_codes?: number[];
  price: number;
  supplier_price: number;
  max_discount: number;
  tier: number | null;
  warranty_months: number | null;
  taxable: boolean;
};

export type BulkCreateHearingAidsInput = {
  rows: BulkCreateHearingAidRow[];
};

export type BulkCreateHearingAidsResponse = {
  created: {
    index: number;
    product: number;
    master_product: number;
  }[];
  skipped: {
    index: number;
    model: string;
    reason: string;
  }[];
};

const SAMPLE_FILE_FALLBACK_FILENAME = "suno-hearing-aid-sample.xlsx";

// Reads the filename out of Content-Disposition, falling back to a fixed name
// (same pattern as the contact-io import template download).
const readAttachmentFilename = (response: Response, fallback: string) => {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return fallback;

  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const parsed = match?.[1]?.replace(/['"]/g, "").trim();

  return parsed || fallback;
};

const extendedMasterProductsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getMasterProduct: builder.query<MasterProduct, number>({
      query: (id) => `/master-products/${id}/`,
      providesTags: ["MasterProducts"]
    }),
    getMasterProducts: builder.query<MasterProductsResponse, ProductsFilters>({
      query: ({ query = "{*}", limit = 100, search, ...filterParams }) =>
        `/master-products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated(
          { ...filterParams, search: search && encodeURIComponent(search) }
        )}&query=${query}&ordering=display_name`,
      providesTags: [
        "MasterProducts",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    addMasterProduct: builder.mutation<MasterProduct, MasterProductInput>({
      query: (masterProduct) => ({
        url: `/master-products/`,
        method: "POST",
        body: masterProduct
      }),
      invalidatesTags: [
        "MasterProducts",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    // Each row creates a master product AND a practice product, so the
    // catalog list ("Products") must be invalidated alongside the master
    // product tags.
    bulkCreateHearingAids: builder.mutation<
      BulkCreateHearingAidsResponse,
      BulkCreateHearingAidsInput
    >({
      query: (body) => ({
        url: `/master-products/bulk-create-hearing-aids/`,
        method: "POST",
        body
      }),
      invalidatesTags: [
        "MasterProducts",
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    // Returns the .xlsx itself rather than JSON, so the download happens in the
    // responseHandler and nothing (no Blob) is written to the cache — the query
    // resolves to null. Only reachable under QUICK_ADD_FILE_UPLOAD_FEATURE: the
    // endpoint is new on the backend (the FE-first rule).
    downloadQuickAddSampleFile: builder.query<null, void>({
      query: () => ({
        url: `/master-products/quick-add-sample-file/`,
        cache: "no-cache",
        responseHandler: async (response) => {
          // fetchBaseQuery runs responseHandler for error responses too, then
          // branches on response.ok. Without this guard an error response would
          // download its own JSON body as a spreadsheet.
          if (!response.ok) {
            return response.json().catch(() => null);
          }

          const blob = await response.blob();
          saveAs(
            blob,
            readAttachmentFilename(response, SAMPLE_FILE_FALLBACK_FILENAME)
          );

          return null;
        }
      }),
      keepUnusedDataFor: 0
    }),
    // SN-19690. Read-only preview of what correcting this entry's classification would do.
    // Deliberately a separate endpoint from the apply one, so a frontend talking to a backend
    // that predates this ticket gets a 404 here instead of silently applying the change.
    // `subtype` is omitted rather than sent empty when null: the serializer defaults it to
    // null, and an empty string fails its ChoiceField.
    getCatalogEntryChangeImpact: builder.query<
      CatalogEntryChangeImpact,
      CatalogEntryChangeImpactInput
    >({
      query: ({ id, type, subtype, scope, product }) => {
        const params = new URLSearchParams({
          type: String(type),
          scope
        });
        if (subtype !== null && subtype !== undefined) {
          params.set("subtype", String(subtype));
        }
        if (product !== undefined) {
          params.set("product", String(product));
        }

        return `/master-products/${id}/change-impact/?${params.toString()}`;
      },
      keepUnusedDataFor: 0
    }),
    // Applies the correction. Invalidates Products as well as MasterProducts: a forward-only
    // change creates a new entry AND a new practice product, and retires the old one, so the
    // catalog list is stale either way.
    reclassifyCatalogEntry: builder.mutation<
      ReclassifyCatalogEntryResponse,
      ReclassifyCatalogEntryInput
    >({
      query: ({ id, ...body }) => ({
        url: `/master-products/${id}/reclassify/`,
        method: "POST",
        body
      }),
      invalidatesTags: [
        "MasterProducts",
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    })
  })
});

export const {
  useGetMasterProductsQuery,
  useAddMasterProductMutation,
  useBulkCreateHearingAidsMutation,
  useGetMasterProductQuery,
  useLazyGetMasterProductQuery,
  useLazyDownloadQuickAddSampleFileQuery,
  useGetCatalogEntryChangeImpactQuery,
  useReclassifyCatalogEntryMutation
} = extendedMasterProductsApi;
